import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Loader2, Search, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { when } from './PlatformConsole';
import { RecallSmsBar } from './PlatformRecallSms';

const KIND_LABEL = { pharmacy: 'Pharmacy record', portal: 'Portal account', sale: 'Sale receipt' };

const RevealDialog = ({ target, defaultReason, onClose }) => {
  const [reason, setReason] = useState(defaultReason);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/platform/patients/reveal', { kind: target.kind, id: target.id, reason, note });
      setData(res);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not show details'); }
    finally { setBusy(false); }
  };

  return (
    <div className="pc-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pc-modal" data-testid="pc-reveal-dialog">
        <h3>{data ? data.name : `Show contact details for ${target.name}?`}</h3>
        {!data ? (
          <>
            <p className="pc-sub" style={{ marginBottom: '.9rem' }}>This view is recorded in the audit trail with your name and reason.</p>
            <label className="pc-field">Reason</label>
            <select className="pc-select" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pc-reveal-reason">
              <option value="id_verification">ID verification</option>
              <option value="recall_trace">Recall tracing</option>
            </select>
            <label className="pc-field" style={{ marginTop: '.7rem' }}>Note (optional)</label>
            <textarea className="pc-textarea" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. FDA recall notice ref, caller ID check" data-testid="pc-reveal-note" />
          </>
        ) : (
          <table className="pc-table" style={{ marginTop: '.6rem' }} data-testid="pc-reveal-details">
            <tbody>
              {[['Sex', data.sex], ['Phone', data.phone], ['Email', data.email], ['Address', data.address],
                ['Pharmacies', data.pharmacies.join(', ')], ['Record', KIND_LABEL[data.kind]], ['Logged reason', data.reason]]
                .map(([k, v]) => (
                  <tr key={k}><td className="pc-muted">{k}</td><td data-testid={`pc-reveal-${k.toLowerCase().replace(' ', '-')}`}>{v || '—'}</td></tr>
                ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" className="pc-btn" onClick={onClose} data-testid="pc-reveal-close">{data ? 'Close' : 'Cancel'}</button>
          {!data && (
            <button type="button" className="pc-btn pc-btn-primary" disabled={busy} onClick={submit} data-testid="pc-reveal-submit">
              {busy ? 'Working…' : 'Show details'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PersonCells = ({ p, onReveal }) => (
  <>
    <td>{p.name}<div className="pc-muted">{KIND_LABEL[p.kind]}</div></td>
    <td>{p.sex || '—'}</td>
    <td className="pc-mono">{p.phone_masked || '—'}<div>{p.email_masked}</div></td>
    <td className="pc-num">
      <button type="button" className="pc-btn" disabled={!p.has_contact} onClick={() => onReveal(p)}
        data-testid={`pc-reveal-${p.kind}-${p.id}`}><Eye className="h-3 w-3 inline" /> Reveal</button>
    </td>
  </>
);

const Directory = ({ onReveal }) => {
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [data, setData] = useState(null);

  const load = useCallback((q = '') => {
    setData(null);
    api.get('/platform/patients', { params: { search: q, source } }).then(r => setData(r.data))
      .catch(() => { toast.error('Could not load patients'); setData({ patients: [], count: 0 }); });
  }, [source]);
  useEffect(() => { load(search); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="pc-patients-directory">
      <form className="pc-actions" style={{ marginTop: '1rem' }} onSubmit={(e) => { e.preventDefault(); load(search); }}>
        <input className="pc-input" style={{ maxWidth: 320 }} value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, email or last 4+ digits of phone" data-testid="pc-patients-search" />
        <select className="pc-select pc-compact" value={source} onChange={(e) => setSource(e.target.value)} data-testid="pc-patients-source">
          <option value="all">All patients</option><option value="pharmacy">Pharmacy records</option><option value="portal">Portal accounts</option>
        </select>
        <button className="pc-btn pc-btn-primary" type="submit" data-testid="pc-patients-search-btn"><Search className="h-3 w-3 inline" /> Search</button>
      </form>
      {!data ? <div className="pc-muted" style={{ marginTop: '1rem' }}><Loader2 className="h-4 w-4 animate-spin" /></div> : (
        <div className="pc-card pc-scroll" style={{ marginTop: '1rem' }}>
          <p className="pc-muted" data-testid="pc-patients-count">{data.count} patient(s){data.truncated ? ' · showing first 200, refine your search' : ''}</p>
          <table className="pc-table">
            <thead><tr><th>Name</th><th>Sex</th><th>Contact</th><th /><th>Pharmacies</th><th>Added</th></tr></thead>
            <tbody>
              {data.patients.map(p => (
                <tr key={`${p.kind}-${p.id}`} data-testid={`pc-patient-row-${p.id}`}>
                  <PersonCells p={p} onReveal={onReveal} />
                  <td>{p.pharmacies.join(', ') || '—'}{p.pharmacy_count > 5 && <span className="pc-muted"> +{p.pharmacy_count - 5}</span>}</td>
                  <td>{when(p.created_at)}</td>
                </tr>
              ))}
              {data.patients.length === 0 && <tr><td colSpan={6} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }} data-testid="pc-patients-empty">No patients match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Recall = ({ onReveal, initial }) => {
  const [product, setProduct] = useState(initial?.product || '');
  const [batch, setBatch] = useState(initial?.batch || '');
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (p, b) => {
    setBusy(true);
    try { const { data: res } = await api.get('/platform/patients/recall', { params: { product: p, batch: b } }); setData(res); setTerms({ product: p, batch: b }); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Recall search failed'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (initial?.product || initial?.batch) search(initial.product || '', initial.batch || ''); }, [initial, search]);
  const run = (e) => { e.preventDefault(); search(product, batch); };

  return (
    <div data-testid="pc-patients-recall">
      <form className="pc-actions" style={{ marginTop: '1rem' }} onSubmit={run}>
        <input className="pc-input" style={{ maxWidth: 260 }} value={product} onChange={(e) => setProduct(e.target.value)}
          placeholder="Medicine name" data-testid="pc-recall-product" />
        <input className="pc-input" style={{ maxWidth: 200 }} value={batch} onChange={(e) => setBatch(e.target.value)}
          placeholder="Batch number (exact)" data-testid="pc-recall-batch" />
        <button className="pc-btn pc-btn-primary" type="submit" disabled={busy || !navigator.onLine} data-testid="pc-recall-search-btn">
          {busy ? 'Searching…' : 'Find patients'}</button>
      </form>
      {data && (
        <div className="pc-card pc-scroll" style={{ marginTop: '1rem' }}>
          <p className="pc-muted" data-testid="pc-recall-count">{data.count} sale line(s) · {data.patients} patient(s)</p>
          <table className="pc-table">
            <thead><tr><th>Name</th><th>Sex</th><th>Contact</th><th /><th>Medicine</th><th>Batch</th><th className="pc-num">Qty</th><th>Pharmacy</th><th>Date</th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.sale_id}-${i}`} data-testid={`pc-recall-row-${i}`}>
                  <PersonCells p={r.patient} onReveal={onReveal} />
                  <td>{r.product_name}</td><td className="pc-mono">{r.batch_number || '—'}</td>
                  <td className="pc-num">{r.quantity}</td><td>{r.pharmacy}</td><td>{when(r.date)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={9} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }} data-testid="pc-recall-empty">No sales found for that medicine or batch.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {data && <RecallAdrTable rows={data.adr_reports || []} count={data.adr_count || 0} onReveal={onReveal} />}
      <RecallSmsBar terms={data && (data.count > 0 || data.adr_count > 0) ? terms : null} autoOpen={!!initial?.openSms} />
    </div>
  );
};

const SEV_CLS = { severe: 'pc-pill pc-pill-red', moderate: 'pc-pill pc-pill-amber', mild: 'pc-pill' };

const RecallAdrTable = ({ rows, count, onReveal }) => (
  <div className="pc-card pc-scroll" style={{ marginTop: '1rem' }} data-testid="pc-recall-adr">
    <p className="pc-muted" data-testid="pc-recall-adr-count">{count} patient reaction report(s) for this medicine/batch</p>
    <table className="pc-table">
      <thead><tr><th>Name</th><th>Sex</th><th>Contact</th><th /><th>Medicine</th><th>Batch</th><th>Severity</th><th>Reaction</th><th>Pharmacy</th><th>Reported</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.report_id} data-testid={`pc-recall-adr-row-${r.report_id}`}>
            <PersonCells p={r.patient} onReveal={onReveal} />
            <td>{r.medicine_name}</td><td className="pc-mono">{r.batch_number || '—'}</td>
            <td><span className={SEV_CLS[r.severity] || 'pc-pill'} data-testid={`pc-recall-adr-severity-${r.report_id}`}>{r.severity}</span></td>
            <td style={{ maxWidth: 280 }}>{r.reaction}</td><td>{r.pharmacy}</td><td>{when(r.date)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={10} className="pc-muted" style={{ textAlign: 'center', padding: '1.5rem' }} data-testid="pc-recall-adr-empty">No patient reaction reports match.</td></tr>}
      </tbody>
    </table>
  </div>
);

export const PlatformPatients = () => {
  const [params] = useSearchParams();
  const initial = useMemo(() => (params.get('recall_product') || params.get('recall_batch')
    ? { product: params.get('recall_product') || '', batch: params.get('recall_batch') || '', openSms: params.get('open') === 'sms' }
    : null), [params]);
  const [tab, setTab] = useState(initial ? 'recall' : 'directory');
  const [target, setTarget] = useState(null);
  return (
    <div data-testid="pc-patients">
      <h1 className="pc-h1">Patients</h1>
      <p className="pc-sub"><ShieldCheck className="h-3.5 w-3.5 inline" /> For ID verification and recall tracing only. Contact details stay hidden until you give a reason; every view is logged.</p>
      <div className="pc-actions" style={{ marginTop: '1rem' }}>
        {[['directory', 'Directory'], ['recall', 'Recall trace']].map(([k, label]) => (
          <button key={k} type="button" className={`pc-btn ${tab === k ? 'pc-btn-primary' : ''}`} onClick={() => setTab(k)}
            data-testid={`pc-patients-tab-${k}`}>{label}</button>
        ))}
      </div>
      {tab === 'directory' ? <Directory onReveal={setTarget} /> : <Recall onReveal={setTarget} initial={initial} />}
      {target && <RevealDialog target={target} defaultReason={tab === 'recall' ? 'recall_trace' : 'id_verification'} onClose={() => setTarget(null)} />}
    </div>
  );
};
